/**
 * Workspace · Petty Cash count voucher (S36).
 *
 * Bill-detail-style document composer. The 9-row denomination grid feeds
 * the printable `<CountDoc>`; the difference between counted total and
 * book balance drives the action label:
 *
 *   variance = 0  →  `bankAccounts.markCounted` (no cash_movement written)
 *   variance ≠ 0  →  `cashMovements.post` with `kind=cash_count_adjustment`
 *
 * @see prototype/cash/petty-cash-count.html
 * @see docs/adr/0012-bank-accounts-and-cash-movements.md §10.4
 */
import { useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Check } from "lucide-react";
import { Alert, Button, Card, CardBody } from "@flint/ui";
import type { pettyCash as pettyCashContract } from "@flint/api-contract";
import { trpc } from "../../../../lib/trpc.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import {
  CountDoc,
  DenominationInput,
  EMPTY_COUNTS,
  denominationTotalPaise,
  formatVariance,
  type DenominationCounts,
} from "../../../../features/petty-cash/index.ts";
import { paiseToInr } from "../../../../lib/format.ts";

export const Route = createFileRoute("/(workspace)/cash/petty-cash/count")({
  component: CountVoucherPage,
});

/** Subset of `cashMovements.post` we read. */
interface PostMovementResult {
  awaitingApproval: { summary: string } | null;
}

function CountVoucherPage() {
  const anchorQ = trpc.pettyCash.anchorData.useQuery({});

  if (anchorQ.isLoading) {
    return (
      <WorkspaceShell
        breadcrumbs={[
          { label: "Cash" },
          { label: "Petty cash", to: "/cash/petty-cash" },
          { label: "Count" },
        ]}
      >
        <Card>
          <CardBody>
            <p className="text-sm text-muted">Loading drawer…</p>
          </CardBody>
        </Card>
      </WorkspaceShell>
    );
  }
  if (anchorQ.error || !anchorQ.data) {
    return (
      <WorkspaceShell
        breadcrumbs={[
          { label: "Cash" },
          { label: "Petty cash", to: "/cash/petty-cash" },
          { label: "Count" },
        ]}
      >
        <Alert
          intent="bad"
          title="Couldn't load drawer"
          description={anchorQ.error?.message ?? "Missing data"}
        />
      </WorkspaceShell>
    );
  }

  return <CountForm anchor={anchorQ.data} />;
}

type AnchorData = pettyCashContract.AnchorDataOutput;

function CountForm({ anchor }: { anchor: AnchorData }) {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const drawer = anchor.drawer;

  const [counts, setCounts] = useState<DenominationCounts>(EMPTY_COUNTS);
  const [countDate, setCountDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [witness, setWitness] = useState("");
  const [narration, setNarration] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const bookPaise = BigInt(drawer.balancePaise);
  const countedPaise = useMemo(() => denominationTotalPaise(counts), [counts]);
  // Variance convention: counted − book (positive = over, negative = short).
  const variancePaise = countedPaise - bookPaise;
  const isExact = variancePaise === 0n;
  const varianceFmt = formatVariance(variancePaise);

  /** Shared invalidator — both the variance and zero-variance paths refresh
   *  the same surfaces. */
  async function invalidateAfterCount() {
    await Promise.all([
      utils.pettyCash.anchorData.invalidate(),
      utils.cashMovements.list.invalidate(),
      utils.bankAccounts.list.invalidate(),
      utils.bankAccounts.get.invalidate(),
      utils.briefings.workspaceSignals.invalidate(),
    ]);
  }

  const postAdjustment = trpc.cashMovements.post.useMutation({
    onSuccess: async (res: PostMovementResult) => {
      await invalidateAfterCount();
      if (res.awaitingApproval) {
        setInfo(
          `Queued for founder review (${res.awaitingApproval.summary}). Tracked in the approvals inbox.`,
        );
      } else {
        void navigate({ to: "/cash/petty-cash" });
      }
    },
    onError: (err: { message: string }) => setError(err.message),
  });

  const markCounted = trpc.bankAccounts.markCounted.useMutation({
    onSuccess: async () => {
      await invalidateAfterCount();
      void navigate({ to: "/cash/petty-cash" });
    },
    onError: (err: { message: string }) => setError(err.message),
  });

  const pending = postAdjustment.isPending || markCounted.isPending;

  function submit(e: React.SyntheticEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);

    if (denominationTotalPaise(counts) === 0n && countedPaise === 0n) {
      setError("Enter the piece counts from the physical count.");
      return;
    }

    // Build the wire shape for the denomination payload (DenominationCount
    // from @flint/api-contract). The component state keys on paise (50000,
    // 20000, …); the wire keys on the human-readable RBI labels (d500, d200,
    // …). Translate here so the contract stays operator-readable on the
    // server side and the audit metadata is self-describing.
    const denominationsWire = {
      d500: counts[50000] || 0,
      d200: counts[20000] || 0,
      d100: counts[10000] || 0,
      d50: counts[5000] || 0,
      d20: counts[2000] || 0,
      d10: counts[1000] || 0,
      d5: counts[500] || 0,
      d2: counts[200] || 0,
      d1: counts[100] || 0,
    } as const;

    if (isExact) {
      // Variance is zero — no movement row, just bump `last_counted_at`.
      // Pass through the reconciliation evidence so the counts-history
      // surface can show the full breakdown.
      markCounted.mutate({
        accountId: drawer.id,
        countedAt: new Date(`${countDate}T00:00:00Z`).toISOString(),
        bookBalancePaise: bookPaise.toString(),
        countedPaise: countedPaise.toString(),
        denominations: denominationsWire,
      });
      return;
    }

    // Variance ≠ 0 — post a `cash_count_adjustment` row.
    // Sign convention for `amountPaise`: always positive integer; the kind
    // tells the ledger which side of the drawer it lands on. We forward
    // the absolute variance and let the domain interpret over/short.
    const absPaise = variancePaise < 0n ? -variancePaise : variancePaise;
    const isOver = variancePaise > 0n;
    const computedNarration =
      narration.trim() ||
      `Physical count · ${isOver ? "over" : "short"} by ${paiseToInr(absPaise)}`;

    postAdjustment.mutate({
      kind: "cash_count_adjustment",
      // Over → money in (toAccountId). Short → money out (fromAccountId).
      ...(isOver ? { toAccountId: drawer.id } : { fromAccountId: drawer.id }),
      amountPaise: absPaise.toString(),
      valueDate: countDate,
      narration: computedNarration,
      // Reconciliation evidence — persisted on the audit metadata so the
      // counts-history page can render book / counted / variance plus the
      // denomination grid in the expanded row.
      bookBalancePaise: bookPaise.toString(),
      countedPaise: countedPaise.toString(),
      denominations: denominationsWire,
    });
  }

  const submitLabel = isExact
    ? markCounted.isPending
      ? "Marking…"
      : "Mark counted (variance ₹0)"
    : postAdjustment.isPending
      ? "Posting…"
      : `Post adjustment · ${varianceFmt.text}`;

  return (
    <WorkspaceShell
      breadcrumbs={[
        { label: "Cash" },
        { label: "Petty cash", to: "/cash/petty-cash" },
        { label: "Count" },
      ]}
    >
      <div className="flex flex-col gap-5">
        <header className="flex flex-wrap items-start gap-3 justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <Link
              to="/cash/petty-cash"
              className="inline-grid place-items-center w-9 h-9 rounded-sm border border-line bg-paper text-ink-2 hover:bg-bg no-underline"
              aria-label="Back to petty cash"
            >
              <ArrowLeft size={16} strokeWidth={2} />
            </Link>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold tracking-tight text-ink leading-tight">
                Physical count
              </h1>
              <p className="mt-1 text-xs text-muted">
                {drawer.name} · book balance {paiseToInr(drawer.balancePaise)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button asChild intent="ghost" size="md" className="border border-line">
              <Link to="/cash/petty-cash">Cancel</Link>
            </Button>
            <Button type="button" intent="primary" size="md" onClick={submit} disabled={pending}>
              <Check size={13} strokeWidth={2.4} aria-hidden />
              {submitLabel}
            </Button>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-5">
          {/* LEFT — denomination grid + meta */}
          <form onSubmit={submit} className="flex flex-col gap-5 min-w-0">
            <section className="bg-paper border border-line rounded-md shadow-sm p-5 flex flex-col gap-3">
              <h3 className="text-sm font-semibold text-ink">Count by denomination</h3>
              <p className="text-xs text-muted">
                Enter the number of pieces per denomination. Totals compute live.
              </p>
              <DenominationInput value={counts} onChange={setCounts} />
            </section>

            <section className="bg-paper border border-line rounded-md shadow-sm p-5 flex flex-col gap-3">
              <h3 className="text-sm font-semibold text-ink">Witness &amp; narration</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="flex flex-col gap-1.5">
                  <span className="text-xs font-semibold text-ink-2">Count date</span>
                  <input
                    type="date"
                    value={countDate}
                    onChange={(e) => setCountDate(e.target.value)}
                    className="px-3 py-2 rounded-sm border border-line bg-paper text-sm text-ink focus:border-sky-600 focus:ring-2 focus:ring-sky-100 outline-none"
                  />
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="text-xs font-semibold text-ink-2">Witnessed by</span>
                  <input
                    type="text"
                    value={witness}
                    onChange={(e) => setWitness(e.target.value)}
                    placeholder="Founder or senior employee"
                    className="px-3 py-2 rounded-sm border border-line bg-paper text-sm text-ink placeholder:text-muted focus:border-sky-600 focus:ring-2 focus:ring-sky-100 outline-none"
                  />
                </label>
              </div>
              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-semibold text-ink-2">Narration (optional)</span>
                <textarea
                  value={narration}
                  onChange={(e) => setNarration(e.target.value)}
                  rows={2}
                  placeholder="Auto-generated if blank."
                  className="px-3 py-2 rounded-sm border border-line bg-paper text-sm text-ink placeholder:text-muted focus:border-sky-600 focus:ring-2 focus:ring-sky-100 outline-none resize-vertical"
                />
              </label>
            </section>

            {error ? (
              <Alert intent="bad" title="Couldn't post the count" description={error} />
            ) : null}
            {info ? <Alert intent="info" title="Heads up" description={info} /> : null}
          </form>

          {/* RIGHT — printable count voucher */}
          <aside className="lg:sticky lg:top-4 self-start">
            <CountDoc
              voucherNumber={`PCV-COUNT-${countDate.replace(/-/g, "")}`}
              drawerName={drawer.name}
              countedBy={drawer.operator ?? drawer.custodian ?? "Operator"}
              witness={witness.trim() || null}
              countDate={countDate}
              counts={counts}
              bookBalancePaise={drawer.balancePaise}
            />
          </aside>
        </div>
      </div>
    </WorkspaceShell>
  );
}
